import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useOnSelectionChange } from '@xyflow/react';

import WorkflowItem from './item-wrapper/WorkflowItem';
import { Box } from '@mui/material';

import {
    ReactFlow,
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
    useReactFlow,
    Background,
    Connection,
    type Edge,
    type Node,
    OnNodesChange,
    OnEdgesChange,
    OnConnect,
    Position,
    reconnectEdge,
} from '@xyflow/react';
import { ContextMenuProps, CurrentRunType, ItemSettingsMapType, WorkflowItemsType } from './index.functions';

import Sidebar from './Sidebar';
import ContextMenu from './ContextMenu';
import { DnDProvider, useDnD } from './DnDContext';
import '@xyflow/react/dist/style.css';
import { uid } from 'tools/tools';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workflowAPI } from 'api/workflow';
import { cloneDeep, debounce } from 'lodash';
import { WorkflowContext } from './context';
import useBundleTranslation from 'i18n';
import { getElementFieldValue } from 'components/common/form/formTools';

const DnDFlow = ({
    workflowSettings,
    allItemSettings,
    workflowId,
    workflowType,
}: {
    workflowSettings: string;
    allItemSettings: ItemSettingsMapType;
    workflowId: number;
    workflowType: string;
}) => {
    const { t } = useBundleTranslation(['app/admin/editor/generic-workflow/common']);

    const findSelectedItem = (nodes: Node[]) => {
        const node = nodes.find((n) => n.selected);
        if (node) return node.id;
        return null;
    };

    type WorkflowStateType = {
        nodes: Node[];
        edges: Edge[];
        workflowItems: WorkflowItemsType;
        selectedItem: string | null;
    };

    let workflow: WorkflowStateType = {
        nodes: [],
        edges: [],
        workflowItems: {},
        selectedItem: null,
    };

    if (workflowSettings != null && workflowSettings > '') {
        const tmp = JSON.parse(workflowSettings);

        if (tmp?.nodes?.length > 0) {
            workflow.nodes = tmp.nodes;
            workflow.selectedItem = findSelectedItem(workflow.nodes);
        }
        if (tmp?.edges?.length > 0) workflow.edges = tmp.edges;
        if (tmp?.items !== undefined) workflow.workflowItems = tmp.items;
    }

    const { data: currentRunState, refetch: refetchCurrentRun } = useQuery<any, Error>({
        queryKey: ['currentRun', workflowId],
        queryFn: () => {
            return workflowAPI.getCurrentRun(workflowId).then((data) => {
                return data;
            });
        },
        refetchOnMount: true,
    });

    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const [nodes, setNodes, onNodesChange]: [Node[], any, OnNodesChange] = useNodesState(workflow.nodes);
    const [edges, setEdges, onEdgesChange]: [Edge[], any, OnEdgesChange<Edge>] = useEdgesState(workflow.edges);
    const [workflowItems, setWorkflowItems] = useState<WorkflowItemsType>(workflow.workflowItems);
    const [currentRun, setCurrentRun] = useState<CurrentRunType | null>(null);

    const [selectedItem, setSelectedItem] = useState<string | null>(workflow.selectedItem);
    const [prevSettings, setPrevSettings] = useState<string | null>(workflowSettings);
    const [itemSettingsMap, setItemSettingsMap] = useState<ItemSettingsMapType>(allItemSettings);
    const [prevItemSettingsMap, setPrevItemSettingsMap] = useState<ItemSettingsMapType>(allItemSettings);

    const { screenToFlowPosition } = useReactFlow();
    const [type, setType, nodeRole, setNodeRole] = useDnD();
    const [menu, setMenu] = useState<ContextMenuProps | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!(currentRunState != null)) return;
        setCurrentRun(currentRunState);
    }, [currentRunState]);

    const debouncedSaveStateHandler = useMemo(
        () =>
            debounce((settings: string) => {
                if (prevSettings != settings) {
                    setPrevSettings(settings);
                    workflowAPI.updateSettings(workflowId, { settings: JSON.parse(settings) });
                }
            }, 200),
        [],
    );

    useEffect(() => {
        debouncedSaveStateHandler(JSON.stringify({ nodes, edges, items: workflowItems }));
    }, [nodes, edges, workflowItems]);

    // const queryClient = useQueryClient();
    const saveItemSettings = useCallback(
        (settings: any) => {
            if (!selectedItem) return;

            const settingsStr = JSON.stringify(settings);
            if (undefined !== itemSettingsMap[selectedItem] && itemSettingsMap[selectedItem] === settingsStr) {
                return;
            }

            setItemSettingsMap({ ...itemSettingsMap, [selectedItem]: settingsStr });
        },
        [selectedItem, itemSettingsMap, workflowItems, workflowId],
    );

    const debouncedSaveItemsStateHandler = useMemo(
        () =>
            debounce((itemSettingsStr: string) => {
                const t = JSON.parse(itemSettingsStr);
                if (t.itemSettingsMap) {
                    for (let itemKey in t.itemSettingsMap) {
                        if (
                            undefined === t.prevItemSettingsMap[itemKey] ||
                            t.prevItemSettingsMap[itemKey] != t.itemSettingsMap[itemKey]
                        ) {
                            const item = t.workflowItems[itemKey] ? t.workflowItems[itemKey] : null;
                            workflowAPI
                                .updateItemSettings(workflowId, itemKey, {
                                    item_type: item?.type ?? '',
                                    item_role: item?.role ?? '',
                                    settings: JSON.parse(t.itemSettingsMap[itemKey]),
                                })
                                .then(() => {
                                    setPrevItemSettingsMap(t.itemSettingsMap);
                                });
                        }
                    }
                }
            }, 200),
        [],
    );

    useEffect(() => {
        debouncedSaveItemsStateHandler(JSON.stringify({ itemSettingsMap, prevItemSettingsMap, workflowItems }));
    }, [itemSettingsMap]);

    const onChange = () => {
        let newSelectedItem = findSelectedItem(nodes);
        if (newSelectedItem !== selectedItem) setSelectedItem(newSelectedItem);
    };

    useOnSelectionChange({ onChange });

    const getId = () => uid();
    const onConnect: OnConnect = useCallback(
        (params: Connection) => setEdges((eds: Edge[]): Edge[] => addEdge(params, eds)),
        [],
    );

    const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();

            // check if the dropped element is valid
            if (!type) {
                return;
            }

            // project was renamed to screenToFlowPosition
            // and you don't need to subtract the reactFlowBounds.left/top anymore
            // details: https://reactflow.dev/whats-new/2023-11-10
            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const nodeId = getId();
            setWorkflowItems({
                ...workflowItems,
                [nodeId]: {
                    id: nodeId,
                    role: nodeRole,
                    type: type,
                },
            });

            let newNode: Node = {
                id: nodeId,
                type,
                position,
                data: { label: t(nodeRole) },
                width: 100,
            };

            if ('input' === type) {
                newNode.sourcePosition = Position.Right;
            } else if ('output' === type) {
                newNode.targetPosition = Position.Left;
            } else {
                newNode.sourcePosition = Position.Right;
                newNode.targetPosition = Position.Left;
            }

            setNodes((nds: Node[]) => nds.concat(newNode));
        },
        [screenToFlowPosition, type, setNodes, nodes],
    );

    const onDragStart = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            const nodeType: string = '';
            if (nodeType) {
                setType(nodeType);
                event.dataTransfer.setData('text/plain', nodeType);
                event.dataTransfer.effectAllowed = 'move';
            }
        },
        [setType],
    );

    const edgeReconnectSuccessful = useRef(true);

    const onReconnectStart = useCallback(() => {
        edgeReconnectSuccessful.current = false;
    }, []);

    const onReconnect = useCallback((oldEdge: Edge, newConnection: Connection) => {
        edgeReconnectSuccessful.current = true;
        setEdges((els: Edge[]) => reconnectEdge(oldEdge, newConnection, els));
    }, []);

    const onReconnectEnd = useCallback((_: any, edge: Edge) => {
        if (!edgeReconnectSuccessful.current) {
            setEdges((eds: Edge[]) => eds.filter((e) => e.id !== edge.id));
        }

        edgeReconnectSuccessful.current = true;
    }, []);

    const onNodeContextMenu = useCallback(
        (event: React.MouseEvent, node: Node) => {
            // Prevent native context menu from showing
            event.preventDefault();

            // Calculate position of the context menu. We want to make sure it
            // doesn't get positioned off-screen.
            if (null === ref.current) return;

            const pane = ref?.current?.getBoundingClientRect();
            const menuProps = {
                id: node.id,
                workflowId: workflowId,
                top: event.clientY < pane.height - 200 ? event.clientY - pane.top : undefined,
                left: event.clientX < pane.width - 200 ? event.clientX - pane.left : undefined,
                right: event.clientX >= pane.width - 200 ? pane.left + pane.width - event.clientX : undefined,
                bottom: event.clientY >= pane.height - 200 ? pane.top + pane.height - event.clientY : undefined,
            };

            setMenu(menuProps);
        },
        [setMenu],
    );
    const onPaneClick = useCallback(() => setMenu(null), [setMenu]);

    return (
        <div className="generic-workflow" style={{ width: '1500px' }}>
            <WorkflowContext.Provider
                value={{
                    workflowId,
                    nodes,
                    edges,
                    workflowType,
                    workflowItems,
                    setWorkflowItems,
                    setNodes,
                    currentRun,
                    refetchCurrentRun,
                    selectedItem,
                    setSelectedItem,
                    itemSettingsMap,
                    setItemSettingsMap,
                }}
            >
                <div className="dndflow" style={{ width: '1500px', height: '300px' }}>
                    <div
                        className="reactflow-wrapper"
                        ref={reactFlowWrapper}
                        style={{ width: '1200px', height: '300px', display: 'inline-block' }}
                    >
                        <div style={{ width: '1200px', height: '300px', position: 'relative' }}>
                            <ReactFlow
                                ref={ref}
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                onDrop={onDrop}
                                onDragOver={onDragOver}
                                onDragStart={onDragStart}
                                onReconnect={onReconnect}
                                onReconnectStart={onReconnectStart}
                                onReconnectEnd={onReconnectEnd}
                                onPaneClick={onPaneClick}
                                onNodeContextMenu={onNodeContextMenu}
                                fitView
                            >
                                <Controls />
                                <Background />
                                {menu && (
                                    <ContextMenu
                                        workflowItems={workflowItems}
                                        setWorkflowItems={setWorkflowItems}
                                        onClick={onPaneClick}
                                        {...menu}
                                    />
                                )}
                            </ReactFlow>
                        </div>
                    </div>
                    <Sidebar />
                </div>
                <div style={{ width: '1200px', height: '100%' }}>
                    {selectedItem && <WorkflowItem key={selectedItem} saveItemSettings={saveItemSettings} />}
                </div>
            </WorkflowContext.Provider>
        </div>
    );
};
export default function GenericWorkflow() {
    const { workflowId: routeWorkflowId } = useParams();
    const workflowId: number = Number(routeWorkflowId);

    const { data: workflow, isLoading } = useQuery<any, Error>({
        queryKey: ['workflowState', workflowId],
        queryFn: () => {
            return workflowAPI.getSettings(workflowId).then((data) => {
                return data;
            });
        },
        refetchOnMount: true,
    });

    const { data: allItemSettings, isLoading: isItemsLoading } = useQuery<any, Error>({
        queryKey: [`workflowItemSettings_${workflowId}`],
        queryFn: async () => await workflowAPI.getAllItemSettings(workflowId),
        refetchOnMount: true,
    });

    if (isLoading || isItemsLoading) return null;

    return (
        <Box sx={{ width: '1200px' }}>
            <ReactFlowProvider>
                <DnDProvider>
                    <div style={{ width: '1200px', height: '300px' }}>
                        <DnDFlow
                            workflowSettings={workflow.settings}
                            allItemSettings={allItemSettings}
                            workflowId={workflowId}
                            workflowType={workflow.type}
                        />
                    </div>
                </DnDProvider>
            </ReactFlowProvider>
        </Box>
    );
}
